// app/routes/login.tsx
import { createFileRoute, Navigate } from "@tanstack/react-router";
import { z } from "zod";
import { useAuth0Login } from "../hooks/useAuth0Login";
import { useAuthenticatedUser } from "~/hooks/useAuthenticatedUser";
import { Spinner } from "~/components/ui/spinner";
import { useEffect, useMemo, useState } from "react";
import { consumeJustLoggedOutFlag } from "~/lib/auth-recovery";

/** Decode a JWT payload without verification (Auth0 validates the invitation) */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const base64url = token.split(".")[1];
    if (!base64url) return null;
    // Convert base64url to standard base64 and add padding
    const base64 = base64url.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    const json = atob(padded);
    return JSON.parse(json);
  } catch (e) {
    console.warn("[Login] Failed to decode invite_token JWT:", e);
    return null;
  }
}

export const Route = createFileRoute("/login")({
  validateSearch: z.object({
    // Auth0 organization invitation parameters (legacy format)
    invitation: z.string().optional(),
    organization: z.string().optional(),
    organization_name: z.string().optional(),
    // JWT-encoded invite token (new format with email)
    invite_token: z.string().optional(),
  }),
  component: Login,
});

export function Login() {
  const { user, isLoading: isUserLoading, routing } = useAuthenticatedUser();
  const { isAuthenticated, isLoading: isAuthLoading, login } = useAuth0Login();
  const { invitation, organization, organization_name, invite_token } = Route.useSearch();
  const [hasRedirected, setHasRedirected] = useState(false);
  const [justLoggedOut, setJustLoggedOut] = useState(() => consumeJustLoggedOutFlag());

  // Decode JWT invite token if present (new format with email)
  const inviteData = useMemo(() => {
    if (!invite_token) return null;
    const payload = decodeJwtPayload(invite_token);
    if (!payload) return null;
    const { email, invitation, organization, organizationName } = payload;
    if (
      typeof email !== "string" ||
      typeof invitation !== "string" ||
      typeof organization !== "string"
    ) return null;
    return {
      email,
      invitation,
      organization,
      organizationName: typeof organizationName === "string" ? organizationName : "",
    };
  }, [invite_token]);

  // Resolve invite params: JWT token takes priority, then legacy URL params
  const resolvedInvitation = inviteData?.invitation ?? invitation;
  const resolvedOrganization = inviteData?.organization ?? organization;
  const resolvedOrgName = inviteData?.organizationName ?? organization_name;
  const resolvedEmail = inviteData?.email;

  const isLoading = isAuthLoading || isUserLoading;
  const isAlreadyAuthenticated = !isLoading && isAuthenticated && !!user;

  // Debug: log resolved invite data
  useEffect(() => {
    if (invite_token) {
      console.log("[Login] invite_token present:", !!invite_token);
      console.log("[Login] inviteData:", inviteData);
      console.log("[Login] resolved:", { resolvedInvitation, resolvedOrganization, resolvedEmail, resolvedOrgName });
    }
  }, [invite_token, inviteData, resolvedInvitation, resolvedOrganization, resolvedEmail, resolvedOrgName]);

  // Handle organization invitations
  useEffect(() => {
    if (isAlreadyAuthenticated) return;
    if (resolvedInvitation && resolvedOrganization && !isAuthLoading && !hasRedirected) {
      console.log("[Login] Redirecting to Auth0 with invite params:", {
        organizationId: resolvedOrganization,
        invitation: resolvedInvitation,
        loginHint: resolvedEmail,
      });
      setJustLoggedOut(false);
      setHasRedirected(true);
      login(undefined, {
        organizationId: resolvedOrganization,
        invitation: resolvedInvitation,
        loginHint: resolvedEmail,
      });
    }
  }, [isAlreadyAuthenticated, resolvedInvitation, resolvedOrganization, resolvedEmail, isAuthLoading, hasRedirected, login]);

  // Auto-redirect to Auth0 login (no org context for non-tenant login)
  // Skip if we just completed a logout — the user wants to be logged out,
  // not immediately funneled back to Auth0.
  useEffect(() => {
    if (isAlreadyAuthenticated || justLoggedOut) return;
    if (!isAuthLoading && !resolvedInvitation && !hasRedirected) {
      console.log("[Login] No invite data, redirecting to generic login");
      setHasRedirected(true);
      login("login");
    }
  }, [isAlreadyAuthenticated, justLoggedOut, isAuthLoading, resolvedInvitation, hasRedirected, login]);

  // If authenticated with user, redirect to dashboard (tenant-aware)
  if (isAlreadyAuthenticated) {
    return <Navigate to={(routing?.homePath ?? "/dashboard") as any} replace />;
  }

  // After a normal logout, show a logged-out screen with a button to log in again
  if (justLoggedOut) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-6 p-8">
        <h1 className="text-2xl font-bold text-black">You have been logged out</h1>
        <p className="text-black text-center max-w-md">
          You have been successfully logged out of your account.
        </p>
        <button
          type="button"
          onClick={() => {
            setJustLoggedOut(false);
            login("login", { forcePrompt: true });
          }}
          className="inline-flex items-center justify-center rounded-md bg-brand-green-primary px-6 py-2.5 text-sm font-medium text-white hover:bg-brand-green-primary/90 transition-colors"
        >
          Log in again
        </button>
      </div>
    );
  }

  // Show loading spinner
  return (
    <div className="flex flex-col items-center justify-center min-h-screen gap-4">
      <Spinner className="size-8 text-brand-green-primary" />
      {resolvedInvitation && (
        <p className="text-sm text-muted-foreground">
          Accepting invitation to {resolvedOrgName || "organization"}...
        </p>
      )}
    </div>
  );
}
